import type { DoOrDumpDashboardPayload } from "@/types/do-or-dump-dashboard";
import type { DoOrDumpDerived } from "@/lib/do-or-dump-compute";
import type { DoOrDumpVerdict } from "@/lib/do-or-dump-compute";
import type { HighImpactQuestionInput } from "@/lib/do-or-dump-high-impact-question";

/** Serializable snapshot sent to `/api/do-or-dump/high-impact` and into the model prompt. */
export type DoOrDumpHighImpactBody = {
  categoryPath: string;
  productDifferentiation: string;
  whyYou: string;
  verdict: DoOrDumpVerdict;
  verdictLine: string;
  momentum: number;
  capacity: number;
  unitsForGoal: number | null;
  goalMonthly: number;
  profitPerItem: number;
  monthlyProfitGuess: number;
  ordersPerMonth: number;
  price: number;
  cost: number;
  followers: number;
  avgViews: number;
  acquisitionsLabel: string;
  timeTier: string;
  audienceTier: string;
  acquisitionTier: string;
  hoursPerWeek: number;
  investmentBudgetUsd: number;
  /** Funnel-only: when they want to see cash */
  cashTimelineLabel?: string;
};

export function highImpactBodyFromDashboardPayload(p: DoOrDumpDashboardPayload): DoOrDumpHighImpactBody {
  return {
    categoryPath: p.categoryPath,
    productDifferentiation: p.productDifferentiation,
    whyYou: p.whyYou,
    verdict: p.verdict,
    verdictLine: p.verdictLine,
    momentum: p.momentum,
    capacity: p.capacity,
    unitsForGoal: p.unitsForGoal,
    goalMonthly: p.goalMonthly,
    profitPerItem: p.profitPerItem,
    monthlyProfitGuess: p.monthlyProfitGuess,
    ordersPerMonth: p.ordersPerMonth,
    price: p.price,
    cost: p.cost,
    followers: p.followers,
    avgViews: p.avgViews,
    acquisitionsLabel: p.acquisitionsLabel,
    timeTier: p.timeTier,
    audienceTier: p.audienceTier,
    acquisitionTier: p.acquisitionTier,
    hoursPerWeek: p.hoursPerWeek,
    investmentBudgetUsd: p.investmentBudgetUsd,
  };
}

export function highImpactBodyFromFunnel(args: {
  categoryPath: string;
  productDifferentiation: string;
  whyYou: string;
  derived: DoOrDumpDerived;
  ordersPerMonth: number;
  price: number;
  cost: number;
  followers: number;
  avgViews: number;
  acquisitionsLabel: string;
  goalMonthly: number;
  hoursPerWeek: number;
  investmentBudgetUsd: number;
  cashTimelineLabel?: string | null;
}): DoOrDumpHighImpactBody {
  const d = args.derived;
  return {
    categoryPath: args.categoryPath,
    productDifferentiation: args.productDifferentiation,
    whyYou: args.whyYou,
    verdict: d.verdict,
    verdictLine: d.verdictLine,
    momentum: d.momentum,
    capacity: d.capacity,
    unitsForGoal: d.unitsForGoal,
    goalMonthly: args.goalMonthly,
    profitPerItem: d.profitPerItem,
    monthlyProfitGuess: d.monthlyProfitGuess,
    ordersPerMonth: args.ordersPerMonth,
    price: args.price,
    cost: args.cost,
    followers: args.followers,
    avgViews: args.avgViews,
    acquisitionsLabel: args.acquisitionsLabel,
    timeTier: d.timeTier,
    audienceTier: d.audienceTier,
    acquisitionTier: d.acquisitionTier,
    hoursPerWeek: args.hoursPerWeek,
    investmentBudgetUsd: args.investmentBudgetUsd,
    cashTimelineLabel: args.cashTimelineLabel ?? undefined,
  };
}

export function highImpactBodyToTailInput(b: DoOrDumpHighImpactBody): HighImpactQuestionInput {
  return {
    verdict: b.verdict,
    profitPerItem: b.profitPerItem,
    momentum: b.momentum,
    unitsForGoal: b.unitsForGoal,
    goalMonthly: b.goalMonthly,
    capacity: b.capacity,
    avgViews: b.avgViews,
    followers: b.followers,
    acquisitionTier: b.acquisitionTier,
  };
}
